import { getCurrencyForSiteByLocale } from "@config/site/site-config";
import { GetMultipleProductVariantsBySkusDocument } from "@graphql/generated/sdk";
import type {
    GetMultipleProductVariantsBySkusQuery,
    ProductVariant,
} from "@graphql/generated/types";
import type { GraphQLClient, GraphQLResponse } from "graphql-request";

export type UnavailableProductDetails = {
    productName?: string;
    variant: ProductVariant;
};

type FetchUnavailableProductDetailsInput = {
    skus: string[];
    locale: string;
};

export const mapUnavailableProductDetails = (
    data: GetMultipleProductVariantsBySkusQuery | undefined
): Map<string, UnavailableProductDetails> => {
    const detailsBySku = new Map<string, UnavailableProductDetails>();

    data?.products?.results?.forEach((product) => {
        const current = product?.masterData?.current;
        current?.variants?.forEach((variant) => {
            if (!variant.sku) return;
            detailsBySku.set(variant.sku, {
                productName: current.name ?? undefined,
                // Generated query types don't reflect the actual ProductVariant structure.
                variant: variant as ProductVariant,
            });
        });
    });

    return detailsBySku;
};

export const fetchUnavailableProductDetails = async (
    client: Pick<GraphQLClient, "request">,
    { skus, locale }: FetchUnavailableProductDetailsInput
): Promise<Map<string, UnavailableProductDetails>> => {
    const { data, errors } = await client.request<
        GraphQLResponse<GetMultipleProductVariantsBySkusQuery>
    >(GetMultipleProductVariantsBySkusDocument, {
        locale,
        skus,
        currency: getCurrencyForSiteByLocale(locale),
    });

    if (errors?.length) {
        throw new Error("Fetching unavailable product details failed", { cause: errors });
    }

    return mapUnavailableProductDetails(data);
};
